<template>
    <div
        v-if="value"
        class="tag-wrapper"
        :class="[variantClass, className, { 'has-dot': computedShowDot, filled, dense }]"
    >
        <div class="tag">
            <span v-if="computedShowDot" class="dot" />
            <span class="value" data-testid="tag-value">{{ value }}</span>
            <!-- Trailing content inside the chip: a marker or a small control next to the value. -->
            <slot />
            <i
                v-if="removable"
                class="i-cancel-x ml-0.5 size-3 shrink-0 cursor-pointer bg-center bg-no-repeat opacity-80 hover:opacity-100"
                role="button"
                tabindex="0"
                :title="`Remove ${value} type`"
                :aria-label="`Remove ${value} type`"
                @click.stop="emit('remove')"
                @keydown.enter.stop="emit('remove')"
            />
        </div>
    </div>
    <span v-else>-</span>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import type { TagCellProps } from '../../tables/types';

interface ExtendedTagProps extends TagCellProps {
    className?: string;
    showDot?: boolean;
    removable?: boolean;
    // Solid colored background with white text instead of the outlined default.
    filled?: boolean;
    // Tighter padding / smaller text for compact chip-style tags.
    dense?: boolean;
}

const props = withDefaults(defineProps<ExtendedTagProps>(), {
    variant: 'default',
    className: '',
    showDot: undefined,
    removable: false,
    filled: false,
    dense: false,
});

const emit = defineEmits<{ (e: 'remove'): void }>();

// Predefined variant mappings
const variantMap: Record<string, string> = {
    default: 'variant-default',
    success: 'variant-success',
    'success-muted': 'variant-success-muted',
    warning: 'variant-warning',
    danger: 'variant-danger',
    info: 'variant-info',
    hold: 'variant-hold',
    done: 'variant-done',
    wip: 'variant-wip',
    silver: 'variant-silver',
    gold: 'variant-gold',
    bronze: 'variant-bronze',
    gray: 'variant-gray',
};

// Status-based variants (auto-detect from value)
const statusVariantMap: Record<string, string> = {
    'Done': 'variant-done',
    'Resolved': 'variant-done',
    'Cancelled': 'variant-done',
    'Canceled': 'variant-done',
    'Closed': 'variant-done',
    'In progress': 'variant-wip',
    'IN PROGRESS': 'variant-info',
    'APPROVED': 'variant-success',
    'Waiting for start': 'variant-hold',
    'Pending': 'variant-hold',
    'Review': 'variant-hold',
    'Summary': 'variant-hold',
    'Technical': 'variant-hold',
    'Billing': 'variant-success',
    'Active': 'variant-info',
    'Enabled': 'variant-success',
    'Disabled': 'variant-danger',
};

const variantClass = computed(() => {
    if (props.value && statusVariantMap[props.value]) {
        return statusVariantMap[props.value];
    }
    return variantMap[props.variant] || variantMap.default;
});

const computedShowDot = computed(() => {
    return props.showDot;
});
</script>

<style scoped>
@reference '@/assets/styles/default.css';

.tag-wrapper {
    @apply flex w-fit text-xs font-semibold;

    /* Soft-chip contract (ITDEVELOP-3107): tinted --tag-*-bg + strong
       --tag-*-fg from the token block, no visible border. The transparent
       border stays for geometry so chips keep their pre-overhaul box. */
    --tag-bg: var(--tag-info-bg);
    --tag-border: var(--tag-info-border);
    --tag-text: var(--tag-info-fg);
    --tag-dot: var(--tag-info-fg);

    &.variant-success,
    &.variant-done,
    &.variant-wip {
        --tag-bg: var(--tag-success-bg);
        --tag-border: var(--tag-success-border);
        --tag-text: var(--tag-success-fg);
        --tag-dot: var(--tag-success-fg);
    }
    &.variant-success-muted {
        --tag-bg: var(--tag-success-muted-bg);
        --tag-border: var(--tag-success-muted-border);
        --tag-text: var(--tag-success-muted-fg);
        --tag-dot: var(--tag-success-muted-fg);
    }
    &.variant-warning, &.variant-hold {
        --tag-bg: var(--tag-warning-bg);
        --tag-border: var(--tag-warning-border);
        --tag-text: var(--tag-warning-fg);
        --tag-dot: var(--tag-warning-fg);
    }
    &.variant-danger {
        --tag-bg: var(--tag-danger-bg);
        --tag-border: var(--tag-danger-border);
        --tag-text: var(--tag-danger-fg);
        --tag-dot: var(--tag-danger-fg);
    }
    &.variant-silver {
        --tag-bg: var(--tag-silver-bg);
        --tag-border: var(--tag-silver-border);
        --tag-text: var(--tag-silver-fg);
        --tag-dot: var(--tag-silver-fg);
    }
    &.variant-gold {
        --tag-bg: var(--tag-gold-bg);
        --tag-border: var(--tag-gold-border);
        --tag-text: var(--tag-gold-fg);
        --tag-dot: var(--tag-gold-fg);
    }
    &.variant-bronze {
        --tag-bg: var(--tag-bronze-bg);
        --tag-border: var(--tag-bronze-border);
        --tag-text: var(--tag-bronze-fg);
        --tag-dot: var(--tag-bronze-fg);
    }
    &.variant-gray {
        --tag-bg: var(--tag-neutral-bg);
        --tag-border: var(--tag-neutral-border);
        --tag-text: var(--tag-neutral-fg);
        --tag-dot: var(--tag-neutral-fg);
    }

    .tag {
        @apply p-xs gap-xs flex items-center rounded-xsm transition-all duration-200 border;
        border-color: var(--tag-border);
        background-color: var(--tag-bg);
        color: var(--tag-text);

        .dot {
            @apply size-2 rounded-full;
            background-color: var(--tag-dot);
        }
    }

    &.filled .tag {
        background-color: var(--tag-dot);
        /* on-accent, not white: dark-mode dot colours are light pastels, so
           the label needs the same ink inversion as the primary button. */
        color: var(--text-on-accent);
        border-color: transparent;
    }

    &.dense {
        @apply text-[0.625rem];

        .tag {
            @apply px-2xs py-0.5 gap-2xs;
        }
    }

    &.has-dot .tag {
        /* Status-cell dot style keeps its bare look: no tint, no border. */
        background-color: transparent;
        border-color: transparent;
        color: var(--tag-dot);
        padding-left: 0;
        @apply uppercase;
    }
}
</style>